import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

// Veolia themed SweetAlert2 configuration
const VeoliaTheme = {
  customClass: {
    popup: 'veolia-modal',
    title: 'veolia-modal-title',
    htmlContainer: 'veolia-modal-content',
    confirmButton: 'veolia-modal-btn veolia-modal-btn-confirm',
    cancelButton: 'veolia-modal-btn veolia-modal-btn-cancel',
    denyButton: 'veolia-modal-btn veolia-modal-btn-deny',
    actions: 'veolia-modal-actions',
    icon: 'veolia-modal-icon'
  },
  buttonsStyling: false,
  showClass: {
    popup: 'veolia-modal-show'
  },
  hideClass: {
    popup: 'veolia-modal-hide'
  }
}

// Export themed Swal for use elsewhere
export const VeoliaSwal = Swal.mixin(VeoliaTheme)

// Global alert functions
export const Toast = Swal.mixin({
  ...VeoliaTheme,
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
})

// Make available globally for easy access
window.VeoliaSwal = VeoliaSwal
window.VeoliaToast = Toast

export default class extends Controller {
  static values = {
    title: { type: String, default: "" },
    text: { type: String, default: "" },
    confirmText: { type: String, default: "Confirmar" },
    cancelText: { type: String, default: "Cancelar" },
    icon: { type: String, default: "warning" },
    variant: { type: String, default: "danger" } // danger, warning, info
  }

  connect() {
    // For Turbo forms, intercept the submit event
    if (this.element.tagName === 'FORM') {
      this.element.addEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  disconnect() {
    if (this.element.tagName === 'FORM') {
      this.element.removeEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  // For buttons/links with data-action="confirm#show"
  async show(event) {
    event.preventDefault()
    event.stopPropagation()

    const confirmed = await this.showConfirmation()

    if (confirmed) {
      this.executeAction(event)
    }
  }

  // For forms
  async handleSubmit(event) {
    if (this.confirmed) {
      this.confirmed = false
      return // Allow the form to submit
    }

    event.preventDefault()
    event.stopPropagation()

    const confirmed = await this.showConfirmation()

    if (confirmed) {
      this.confirmed = true
      // Re-submit the form
      if (this.element.requestSubmit) {
        this.element.requestSubmit()
      } else {
        this.element.submit()
      }
    }
  }

  async showConfirmation() {
    const result = await VeoliaSwal.fire({
      title: this.titleValue || this.getDefaultTitle(),
      text: this.textValue || this.getDefaultText(),
      icon: this.iconValue,
      showCancelButton: true,
      confirmButtonText: this.confirmTextValue,
      cancelButtonText: this.cancelTextValue,
      reverseButtons: true,
      focusCancel: true
    })

    return result.isConfirmed
  }

  executeAction(event) {
    const element = event.currentTarget || this.element

    // Handle link clicks
    if (element.tagName === 'A') {
      // Check for Turbo method
      const method = element.dataset.turboMethod
      if (method) {
        // Let Turbo handle it by clicking the link
        element.removeAttribute('data-action')
        element.click()
        element.setAttribute('data-action', 'confirm#show')
      } else {
        window.location.href = element.href
      }
    }
    // Handle button inside form
    else if (element.tagName === 'BUTTON' && element.form) {
      element.form.requestSubmit ? element.form.requestSubmit() : element.form.submit()
    }
    // Handle form buttons (button_to)
    else if (element.closest('form')) {
      const form = element.closest('form')
      form.requestSubmit ? form.requestSubmit() : form.submit()
    }
  }

  getDefaultTitle() {
    switch (this.variantValue) {
      case 'danger':
        return '¿Estás seguro?'
      case 'warning':
        return 'Atención'
      case 'info':
        return 'Confirmar acción'
      default:
        return '¿Estás seguro?'
    }
  }

  getDefaultText() {
    switch (this.variantValue) {
      case 'danger':
        return 'Esta acción no se puede deshacer.'
      case 'warning':
        return '¿Deseas continuar con esta acción?'
      case 'info':
        return 'Por favor confirma para continuar.'
      default:
        return 'Esta acción no se puede deshacer.'
    }
  }
}
